import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { Row } from 'components/shared/Layout';
import Tabs from 'components/ui/TabsRefund';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { getBookingJourneysURL } from 'utils/urls';
import { isUserThalysAgent } from 'utils/booking';
import { codes, methods, ADDITIONAL_DETAILS_REFUND_KEY_PREFIX } from 'constants/payment';
import { historyShape, paymentsShape, userShape, hotelsShape } from 'constants/propTypes';

import PaymentMethod from 'pages/BookingDetails/Payments/PaymentMethod';
import ReviewBookingSummary from 'components/ui/ReviewBookingSummary';
import {
  getAvailableRefundMethods,
  getRefundTabsForBooking,
  findSuccessRefundsMadeAgainstRefId,
  getRefundRefId,
  calcTotalToBeRefunded,
  getRefundAmount,
  findS3Refunds,
  isBookingEHL,
  paymentsTabsTypesForEHL,
  getAmountToRefundPerMethod,
  getIsPackageBookingOnly
} from './Payments.utils';

import {
  PaymentSummary,
  PaymentContainer,
  PaymentSelectionContainer,
  StyledPaymentContainer,
  StyledAmount,
  StyledPayments,
  StyledSpacedApart,
  StyledAmountDue,
  Divider
} from './Payments.styles';

import { handleRefundSubmit } from './SplitPayment/SplitPayment.utils';

import RefundComponents from './RefundComponents';

const Refunds = ({
  history: { location },
  availableMethods,
  processCardPaymentRefund,
  addPayment,
  addPayoutAndBalance,
  updatePayments,
  loading,
  currency,
  reference,
  customer,
  payments,
  refunds,
  isPackage,
  hotels,
  user,
  additionalDetails = [],
  isRefundingEntireBlueVoucherBooking = false
}) => {
  const refId = getRefundRefId(payments);
  const refundAmount = getRefundAmount(payments);
  const partialRefunds = findSuccessRefundsMadeAgainstRefId(refId)(refunds);
  const toBeRefunded = calcTotalToBeRefunded(payments, refunds, refId);
  const isThalysAgent = isUserThalysAgent(user);
  const isOverpaid = payments?.find((p) => p.refId === refId)?.reference?.endsWith('_overpaid') ?? false;

  const refundMethods = getAvailableRefundMethods({
    availableMethods,
    payments,
    isBookingEHL: isBookingEHL(additionalDetails),
    isThalysAgent
  });
  const isRefundingFeeOrInsurance = new URLSearchParams(location.search).get('isRefundingFeeOrInsurance');
  const membershipNumber = new URLSearchParams(location.search).get('membershipNumber');

  const isPackageBookingOnly = getIsPackageBookingOnly(additionalDetails);
  const tabs = isBookingEHL(additionalDetails)
    ? paymentsTabsTypesForEHL(getRefundTabsForBooking(refundMethods), isPackageBookingOnly)
    : getRefundTabsForBooking(refundMethods);

  const loyaltyPayments = payments.filter((p) => p.method === codes.LOYALTY_POINTS && p.isSuccessful && !p.isPayout && !p.isBalance);

  const hasLoyaltyPaymentsToRefund =
    loyaltyPayments
      .map(({ id, amountInOriginalCurrency }) => {
        const loyaltyPayoutAmount = additionalDetails
          .filter((ad) => ad.itemRef === id && ad.key.includes(ADDITIONAL_DETAILS_REFUND_KEY_PREFIX))
          .reduce((payout, { value }) => payout + Number(value), 0);

        const availableAmount = amountInOriginalCurrency - loyaltyPayoutAmount;

        return availableAmount;
      })
      .filter((availableAmount) => availableAmount).length > 0;

  const isStationUser = user?.paymentLocation?.isStation;
  const userDefaultCurrency = user?.paymentCurrencies?.[0] || 'GBP';

  let shouldHideBlueVoucherRefund = false;

  if (isRefundingEntireBlueVoucherBooking) {
    const bvRefundLeft = getAmountToRefundPerMethod(payments, codes.BLUE_VOUCHER);

    if (bvRefundLeft === 0) {
      shouldHideBlueVoucherRefund = true;
    }
  }

  let refundTabs = tabs.map((tab) => ({
    ...tab,

    disabled:
      (!isRefundingFeeOrInsurance && !isOverpaid && hasLoyaltyPaymentsToRefund && tab.value !== methods.LOYALTY_POINTS) ||
      Boolean(isStationUser && currency !== userDefaultCurrency && tab.value === methods.CASH) ||
      (isRefundingEntireBlueVoucherBooking && !shouldHideBlueVoucherRefund && tab.value !== methods.BLUE_VOUCHER),
    disabledOnHoverMessage:
      isStationUser && currency !== userDefaultCurrency && tab.value === methods.CASH
        ? 'stations.cashLocalCurrencyOnly'
        : isRefundingEntireBlueVoucherBooking && !shouldHideBlueVoucherRefund
        ? 'refunds.firstRefundBlueVoucherWarning'
        : 'refunds.firstRefundPointsWarning'
  }));

  if (shouldHideBlueVoucherRefund) {
    refundTabs = refundTabs.filter((tab) => tab.value !== methods.BLUE_VOUCHER);
  }

  const defaultMethod = refundTabs.filter((tab) => !tab.disabled)[0]?.value || refundMethods[0]?.code;
  const hash = location.hash ? location.hash.substring(1) : defaultMethod;

  const [paymentMethod, changePaymentMethod] = useState(hash);

  if (toBeRefunded <= 0 || findS3Refunds(payments).length < 1) {
    return <Redirect to={getBookingJourneysURL(reference, membershipNumber)} />;
  }

  return (
    <ReviewBookingSummary title="payments.makeRefund">
      <Row>
        <PaymentSelectionContainer alignItems="center">
          <h2>
            <FormattedMessage id="payments.refundTypes" />
          </h2>
          <Tabs
            overflowScroll={true}
            payments={payments}
            currency={currency}
            onClick={changePaymentMethod}
            selectedValue={paymentMethod || refundMethods[0].code}
            tabs={refundTabs}
          />
        </PaymentSelectionContainer>

        <PaymentContainer columns={12}>
          <PaymentSummary span={5}>
            <StyledAmount data-cy="amount-due">
              <h3>
                <FormattedMessage id="payments.refundAmount" />
              </h3>
              <StyledAmountDue>
                <FormattedCurrency amount={refundAmount} currency={currency} />
              </StyledAmountDue>
              <h4>
                <FormattedMessage id="refunds.title" />
              </h4>
            </StyledAmount>
            <StyledPayments data-cy="provisional-payments">
              {partialRefunds.map((refund) => (
                <li key={refund.id}>
                  <StyledSpacedApart>
                    <PaymentMethod method={refund.method} />
                    <Divider />
                    <FormattedCurrency amount={refund.amount} currency={currency} />
                  </StyledSpacedApart>
                </li>
              ))}
            </StyledPayments>
            <StyledSpacedApart data-cy="remaining-refund">
              <FormattedMessage id="payments.totalOutstanding" />
              <Divider />
              <FormattedCurrency amount={toBeRefunded} currency={currency} />
            </StyledSpacedApart>
          </PaymentSummary>

          <StyledPaymentContainer span={7}>
            <RefundComponents
              handleRefundSubmit={handleRefundSubmit}
              addPayment={addPayment}
              addPayoutAndBalance={addPayoutAndBalance}
              bookingReference={reference}
              currency={currency}
              customer={customer}
              loading={loading}
              method={paymentMethod.substring(paymentMethod.indexOf('_') + 1)}
              reference={refId}
              totalPriceToBePaid={toBeRefunded}
              updatePayments={updatePayments}
              processCardPaymentRefund={processCardPaymentRefund}
              paymentLocation={user.paymentLocation}
              payments={payments}
              isPackage={isPackage}
              hotels={hotels}
              additionalDetails={additionalDetails}
              username={user.username}
              isRefundingEntireBlueVoucherBooking={isRefundingEntireBlueVoucherBooking}
              isOverpaid={isOverpaid}
            />
          </StyledPaymentContainer>
        </PaymentContainer>
      </Row>
    </ReviewBookingSummary>
  );
};

Refunds.propTypes = {
  addPayment: PropTypes.func,
  addPayoutAndBalance: PropTypes.func,
  availableMethods: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string
    })
  ),
  customer: PropTypes.shape({
    email: PropTypes.string
  }),
  currency: PropTypes.string,
  history: historyShape,
  loading: PropTypes.bool,
  payments: paymentsShape,
  processCardPaymentRefund: PropTypes.func,
  reference: PropTypes.string,
  refunds: paymentsShape,
  updatePayments: PropTypes.func,
  user: userShape,
  isPackage: PropTypes.bool,
  hotels: hotelsShape,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  isRefundingEntireBlueVoucherBooking: PropTypes.bool
};

export default Refunds;
